import { useLayoutEffect, useRef, useState } from 'react';
import { Neutral500, Neutral600 } from '@src/utils/colors';

export const YEAR_MARKER_STROKE = Neutral500;
const YEAR_LABEL_FILL = Neutral600;
const YEAR_LABEL_FONT_SIZE = 10;
const YEAR_LABEL_FONT_WEIGHT = 500;
// Approximate half-width in px of a 4-digit year at the label's font size/weight.
// Used to detect when the centered label would overflow the chart's plot area.
const YEAR_LABEL_HALF_WIDTH = 14;

export const useElementWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
};

interface YearMarkerLabelProps {
  // viewBox is injected by recharts via cloneElement on the label prop
  viewBox?: { x?: number; y?: number };
  year: string;
  chartWidth: number;
  marginLeft: number;
  marginRight: number;
  yAxisWidth: number;
}

export const YearMarkerLabel = ({
  viewBox,
  year,
  chartWidth,
  marginLeft,
  marginRight,
  yAxisWidth
}: YearMarkerLabelProps) => {
  if (!viewBox || typeof viewBox.x !== 'number' || typeof viewBox.y !== 'number') return null;
  const lineX = viewBox.x;
  const labelY = viewBox.y - 4;
  let textAnchor: 'start' | 'middle' | 'end' = 'middle';
  if (chartWidth > 0) {
    const plotLeft = marginLeft + yAxisWidth;
    const plotRight = Math.max(plotLeft, chartWidth - marginRight);
    if (lineX + YEAR_LABEL_HALF_WIDTH > plotRight) {
      textAnchor = 'end';
    } else if (lineX - YEAR_LABEL_HALF_WIDTH < plotLeft) {
      textAnchor = 'start';
    }
  }
  return (
    <text
      x={lineX}
      y={labelY}
      textAnchor={textAnchor}
      fill={YEAR_LABEL_FILL}
      fontSize={YEAR_LABEL_FONT_SIZE}
      fontWeight={YEAR_LABEL_FONT_WEIGHT}
    >
      {year}
    </text>
  );
};
